import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  BreadcrumbContext,
  CircleContext,
  LoggedContext,
  NotifContext,
  NotifNbreContext,
} from './Context';
import Chatbox from './pages/Chatbox';
import Compte from './pages/Compte';
import CompteParticipant from './pages/CompteParticipant';
import Contacts from './pages/Contacts';
import Dashboard from './pages/Dashboard';
import Journee from './pages/Journee';
import Login from './pages/Login';
import Main from './pages/Main';
import Messages from './pages/Messages';
import Participants from './pages/Participants';
import Programme from './pages/Programme';
import ProgrammeDetail from './pages/ProgrammeDetail';
// import { clientWebsocketNotifications } from './websocket';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { participantsRedux } from './redux/evenementsServices';
import { setParticipantsCache } from './redux/evenementsSlice';
import ClotureCompte from './pages/ClotureCompte';
import CompteDeleted from './pages/CompteDeleted';
import ConditionsLegales from './pages/ConditionsLegales';
import Copyright from './pages/Copyright';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import NotificationsPage from './pages/NotificationsPage';
import NousContacter from './pages/NousContacter';
import Parametres from './pages/Parametres';
import PartenaireDetail from './pages/PartenaireDetail';
import PartenairesExpo from './pages/PartenairesExpo';
import PlanStand from './pages/PlanStand';
import Principale from './pages/Principale';
import MesRendezVous from './pages/MesRendezVous';
import Rendezvous from './pages/Rendezvous';
import SuppressionCompte from './pages/SuppressionCompte';
import { SOCKETURL } from './redux/api';
import { NotificationsRedux } from './redux/profilService';
import { addHistorique } from './redux/profilSlice';
import {
  capture,
  captureDuration,
  capturePageview,
  identifyUser,
  setAnalyticsContext,
} from './services/analytics';
import MobileAppBanner from './components/MobileAppBanner';
import MagicLanding from './pages/MagicLanding';
import CompanionLanding from './pages/CompanionLanding';
import CompanionSession from './components/CompanionSession';
import CompanionRestriction from './components/CompanionRestriction';
import AccountOnly from './components/AccountOnly';
import CompanionRoute from './components/CompanionRoute';
import moment from 'moment';
import 'moment/locale/fr';

moment.locale('fr');

function App() {
  const souvenir = useSelector(state => state.user.souvenir);
  const [logged, setLogged] = useState(souvenir);
  const [circle, setCircle] = useState(false);
  const [message, setMessage] = useState(null);
  const [nbre, setNbre] = useState(0);
  const [pages, setPages] = useState([{ url: '/', nom: 'Accueil' }]);

  const userToken = useSelector(state => state.user.access);
  const isAuth = !!userToken && userToken !== '';
  const companion = useSelector(state => state.companion);
  const isCompanion = Boolean(companion.token) && !isAuth;
  const monProfil = useSelector(state => state.profil.profil);
  const evenementSelected = useSelector(state => state.evenements.evenementSelect);
  const evenements = useSelector(state => state.evenements.evenements);
  const participantsCache = useSelector(state => state.evenements.participantsCache);

  // const nbreNotif = useContext(NotifNbreContext)
  const dispatch = useDispatch();
  const preloadStartedRef = useRef(false);

  const location = useLocation();

  // const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(userToken === '' ? null : `${SOCKETURL}/notification/?token=${userToken}`,
  const { readyState, getWebSocket } = useWebSocket(
    !isAuth ? null : `${SOCKETURL}/notification/?token=${userToken}`,
    {
      onOpen: () => {
        console.log('Websocket notifications connecté');
      },
      onMessage: event => {
        let parsed;
        try {
          parsed = JSON.parse(event?.data);
        } catch (_e) {
          parsed = {};
        }
        if (
          parsed.type === 'notification' ||
          parsed.type === 'notification_read' ||
          parsed.type === 'notification_archived'
        ) {
          refreshNotifications();
        }
      },
      onError: () => {
        console.log('Erreur avec le websocket');
        // App()
        getWebSocket();
      },
      onClose: () => {
        console.log('Websocket fermé');
        // App()
        getWebSocket();
      },
    }
  );

  const unreadNotificationsWithDataTarget = useSelector(state =>
    state.profil.notifications.filter(item => item.data_target !== null)
  );
  const profilRdv = useSelector(state => state.profil.rdv);
  const canAccessRendezvousRoute =
    unreadNotificationsWithDataTarget.length > 0 ||
    (profilRdv &&
      typeof profilRdv === 'object' &&
      profilRdv !== null &&
      profilRdv.data_target != null);

  const history = useSelector(state => state.profil.historique);

  // Helper to fetch notifications and update state
  const refreshNotifications = useCallback(() => {
    if (!isAuth || !monProfil?.id) return;
    NotificationsRedux(userToken)
      .then(reponse => {
        const filtered = reponse.data.filter(
          item => item.participant_recepteur.id === monProfil.id && item.etat === false
        );
        dispatch({
          type: 'Profil/addNotifications',
          payload: filtered,
        });
        setNbre(filtered.length);
      })
      .catch(erreur => {
        console.log('Error fetching notifications:', erreur);
      });
  }, [isAuth, monProfil?.id, userToken, dispatch]);

  // Fetch notifications on initial load and on every route change (cross-device sync)
  // When user reads a notification on mobile, the next navigation on web will sync the count
  useEffect(() => {
    if (isAuth && monProfil?.id) {
      refreshNotifications();
    }
  }, [isAuth, monProfil?.id, location.pathname, refreshNotifications]);

  // Also re-fetch when browser tab regains focus (e.g. user switched tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuth && monProfil?.id) {
        refreshNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuth, monProfil?.id, userToken, refreshNotifications]);

  // Preload participants for all events when WebSocket connects (page 1 per event, memory-limited)
  useEffect(() => {
    if (
      !isAuth ||
      !userToken ||
      readyState !== ReadyState.OPEN ||
      !Array.isArray(evenements) ||
      evenements.length === 0 ||
      preloadStartedRef.current
    ) {
      return;
    }
    preloadStartedRef.current = true;
    const eventIds = evenements.map(e => e.id).filter(Boolean);
    const cache = participantsCache ?? {};
    const toFetch = eventIds.filter(id => !cache[id]).slice(0, 10);
    if (toFetch.length === 0) return;
    toFetch.forEach(eventId => {
      participantsRedux(userToken, eventId)
        .then(res => {
          dispatch(setParticipantsCache({ eventId, data: res.data }));
        })
        .catch(() => {
          // Silent fail, cache will be populated on demand
        });
    });
  }, [isAuth, userToken, readyState, evenements, participantsCache, dispatch]);

  useEffect(() => {
    capturePageview(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const role =
      monProfil?.profil?.description ||
      monProfil?.profil?.nom ||
      monProfil?.role ||
      monProfil?.type ||
      undefined;
    setAnalyticsContext({
      event_id: evenementSelected?.id,
      role: role ? String(role).trim().toLowerCase() : undefined,
    });
    if (isAuth && monProfil?.id) {
      identifyUser(monProfil.id, {
        event_id: evenementSelected?.id,
        role: role ? String(role).trim().toLowerCase() : undefined,
      });
    }
  }, [evenementSelected?.id, identifyUser, isAuth, monProfil]);

  useEffect(() => {
    const screenName = location.pathname || '/';
    const startedAt = performance.now();
    capture('screen_viewed', { screen_name: screenName });
    return () => {
      captureDuration(screenName, performance.now() - startedAt);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuth) {
      if (
        location.pathname !== '/' &&
        (location.pathname === '/contacts' ||
          location.pathname === '/notifications' ||
          location.pathname === '/rendezvous' ||
          location.pathname === '/mes-rendez-vous')
      ) {
        dispatch(addHistorique(location.pathname));
        // if (location.pathname === '/rendezvous') {
        //   dispatch(addHistorique('/notifications'))
        // }
      } else {
        dispatch(addHistorique('/'));
      }

      // lien = location.pathname
    }
    // console.log(history)
    // console.log(souvenir)
    // console.log(pages)
    // console.log(location.pathname)

    // if (!souvenir) {
    //   dispatch(resetUser())
    // }

    // getWebSocket().onmessage =
    // getWebSocket().onerror =
    // getWebSocket().onclose =
  }, [souvenir, location.pathname, isAuth, dispatch]);

  return (
    <div>
      <CompanionSession />
      <CompanionRestriction />
      {location.pathname !== '/magic' && !location.pathname.startsWith('/companion') && (
        <MobileAppBanner />
      )}
      <LoggedContext.Provider value={{ logged: logged, setLogged: setLogged }}>
        <NotifContext.Provider value={{ message: message, setMessage: setMessage }}>
          <CircleContext.Provider value={{ circle: circle, setCircle: setCircle }}>
            <NotifNbreContext.Provider value={{ nbre: nbre, setNbre: setNbre }}>
              <BreadcrumbContext.Provider value={{ pages: pages, setPages: setPages }}>
                <Routes>
                  <Route
                    exact
                    path="/"
                    element={
                      isCompanion && !isAuth ? (
                        <Navigate to={`/dashboard/${companion.event.id}`} replace />
                      ) : logged === true && isAuth ? (
                        <Navigate to={'/main'} />
                      ) : isAuth ? (
                        <Navigate to={'/main'} />
                      ) : (
                        <Principale />
                      )
                    }
                  ></Route>
                  <Route path="/conditionsLegales/:login" element={<ConditionsLegales />} />
                  <Route path="/copyright/:login" element={<Copyright />} />
                  <Route
                    path="/politiqueConfidentialite/:login"
                    element={<PolitiqueConfidentialite />}
                  />
                  <Route path="/clotureCompte" element={<ClotureCompte />} />
                  <Route path="/deleted" element={<CompteDeleted />} />
                  <Route path="/magic" element={<MagicLanding />} />
                  <Route path="/companion/*" element={<CompanionLanding />} />
                  {isAuth || isCompanion ? (
                    <>
                      <Route
                        path={'/login'}
                        element={logged === true && isAuth ? <Navigate to={'/main'} /> : <Login />}
                      />
                      <Route
                        path={'/main'}
                        element={
                          isCompanion ? (
                            <Navigate to={`/dashboard/${companion.event.id}`} replace />
                          ) : history === '/' ? (
                            <Main />
                          ) : (
                            <Navigate to={history} />
                          )
                        }
                      />
                      <Route
                        path="/dashboard/:idevent"
                        element={
                          <CompanionRoute>
                            <Dashboard />
                          </CompanionRoute>
                        }
                      />
                      <Route path="/programmes" element={<Programme></Programme>}></Route>
                      <Route
                        path="/programme/:idprogram/:type"
                        element={
                          <CompanionRoute>
                            <ProgrammeDetail />
                          </CompanionRoute>
                        }
                      ></Route>
                      <Route
                        path="/compte"
                        element={
                          <AccountOnly feature="Mon profil">
                            <Compte></Compte>
                          </AccountOnly>
                        }
                      ></Route>
                      <Route path="/participants" element={<Participants></Participants>}></Route>
                      <Route
                        path="/compteParticipant/:idparticipant/:contact"
                        element={
                          <AccountOnly feature="Fiche participant">
                            <CompteParticipant></CompteParticipant>
                          </AccountOnly>
                        }
                      ></Route>
                      <Route
                        path="/majournee"
                        element={
                          <AccountOnly feature="Ma journée">
                            <Journee />
                          </AccountOnly>
                        }
                      />
                      <Route
                        path="/contacts"
                        element={
                          <AccountOnly feature="Contacts">
                            <Contacts />
                          </AccountOnly>
                        }
                      />
                      <Route
                        path="/messagerie"
                        element={
                          <AccountOnly feature="Messages">
                            <Messages />
                          </AccountOnly>
                        }
                      />
                      <Route
                        path="/chat/:chatid/:room/:contact"
                        element={
                          <AccountOnly feature="Messages">
                            <Chatbox />
                          </AccountOnly>
                        }
                      />
                      <Route
                        path="/parametres"
                        element={
                          <AccountOnly feature="Paramètres">
                            <Parametres />
                          </AccountOnly>
                        }
                      />
                      <Route
                        path="/nousContacter"
                        element={
                          <AccountOnly feature="Nous contacter">
                            <NousContacter />
                          </AccountOnly>
                        }
                      />
                      <Route path="/partenaires" element={<PartenairesExpo />} />
                      <Route
                        path="/partenaire/:id/:premium"
                        element={
                          <CompanionRoute>
                            <PartenaireDetail />
                          </CompanionRoute>
                        }
                      />
                      <Route
                        path="/notifications"
                        element={
                          <AccountOnly feature="Notifications">
                            <NotificationsPage />
                          </AccountOnly>
                        }
                      />
                      <Route
                        path="/rendezvous"
                        element={
                          <AccountOnly feature="Rendez-vous">
                            {!canAccessRendezvousRoute ? <Navigate to="/" /> : <Rendezvous />}
                          </AccountOnly>
                        }
                      />
                      <Route
                        path="/mes-rendez-vous"
                        element={
                          <AccountOnly feature="Mes rendez-vous">
                            <MesRendezVous />
                          </AccountOnly>
                        }
                      />
                      <Route path="/planStand/:idPartenaire" element={<PlanStand />} />
                      <Route
                        path="/suppressionCompte"
                        element={
                          <AccountOnly feature="Suppression du compte">
                            <SuppressionCompte />
                          </AccountOnly>
                        }
                      />

                      <Route
                        path="*"
                        element={
                          evenementSelected === '' ? (
                            <Navigate to={'/'} />
                          ) : (
                            <Navigate to={`/dashboard/${evenementSelected.id}`} />
                          )
                        }
                      ></Route>
                    </>
                  ) : (
                    <>
                      {/* <Route path={'/main'} element={isCompanion ? <Navigate to={`/dashboard/${companion.event.id}`} replace /> : history === '/' ? <Main /> : <Navigate to={history} />} /> */}
                      <Route path={'/login'} element={<Login />} />

                      <Route
                        path="*"
                        element={<Navigate to={companion.expired ? '/companion' : '/'} replace />}
                      ></Route>
                    </>
                  )}

                  {/* <Route path='*' element={<Navigate to={'/'} />}></Route> */}
                </Routes>
              </BreadcrumbContext.Provider>
            </NotifNbreContext.Provider>
          </CircleContext.Provider>
        </NotifContext.Provider>
      </LoggedContext.Provider>
    </div>
  );
}

export default App;
